var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getRenderedCommunicationChartNodeRadiusPx, isFiniteCommunicationChartPosition } from '@web/components/CommunicationNetworkGraph/graph/communicationChartArrowGeometry';
import { resolveCommunicationChartArrowWidthPx } from '@web/components/CommunicationNetworkGraph/graph/communicationChartConstants';
import { getEdgeRenderedLineWidthPx } from '@web/components/CommunicationNetworkGraph/graph/drawCommunicationChartArrowOverlay';
var ORG_TEAM_LABEL_MARGIN_PX = 8;
var TWO_PI = Math.PI * 2;
var DEFAULT_LABEL_ANGLE = -Math.PI / 2;
var LINE_BLOCK_HALF_ANGLE_RAD = 0.32;
var ARROW_BLOCK_HALF_ANGLE_RAD = 0.58;
var TOP_ANGLE_BIAS_WEIGHT = 0.45;
var TOP_ANGLE_MIN_CLEARANCE_RAD = 0.08;
var labelPlacementCache = new WeakMap();
var normalizeAngle = function (angle) {
    var normalized = angle % TWO_PI;
    if (normalized < 0) {
        normalized += TWO_PI;
    }
    return normalized;
};
var angleFromCenterToPoint = function (center, point) {
    return Math.atan2(point.y - center.y, point.x - center.x);
};
var readNodeEndpoint = function (edge, nodeId) {
    try {
        var endpoint = void 0;
        if (edge.source().id() === nodeId) {
            endpoint = edge.renderedSourceEndpoint();
        }
        else if (edge.target().id() === nodeId) {
            endpoint = edge.renderedTargetEndpoint();
        }
        return isFiniteCommunicationChartPosition(endpoint) ? endpoint : null;
    }
    catch (_a) {
        return null;
    }
};
var readArrowSizeOnNode = function (edge, nodeId) {
    var _a, _b;
    if (edge.source().id() === nodeId) {
        return ((_a = edge.data('sourceArrowSize')) !== null && _a !== void 0 ? _a : 'none');
    }
    if (edge.target().id() === nodeId) {
        return ((_b = edge.data('targetArrowSize')) !== null && _b !== void 0 ? _b : 'none');
    }
    return 'none';
};
var addBlockedInterval = function (intervals, centerAngle, halfWidth) {
    var start = normalizeAngle(centerAngle - halfWidth);
    var end = normalizeAngle(centerAngle + halfWidth);
    if (start <= end) {
        intervals.push({ start: start, end: end });
        return;
    }
    intervals.push({ start: start, end: TWO_PI });
    intervals.push({ start: 0, end: end });
};
var mergeAngleIntervals = function (intervals) {
    if (intervals.length === 0) {
        return [];
    }
    var sorted = __spreadArray([], intervals, true).sort(function (left, right) { return left.start - right.start; });
    var merged = [sorted[0]];
    for (var index = 1; index < sorted.length; index += 1) {
        var current = sorted[index];
        var last = merged[merged.length - 1];
        if (current.start <= last.end) {
            last.end = Math.max(last.end, current.end);
            continue;
        }
        merged.push(current);
    }
    return merged;
};
var intervalLength = function (interval) {
    if (interval.end >= interval.start) {
        return interval.end - interval.start;
    }
    return TWO_PI - interval.start + interval.end;
};
var midpointAngle = function (start, end) {
    if (end >= start) {
        return normalizeAngle((start + end) / 2);
    }
    var length = TWO_PI - start + end;
    return normalizeAngle(start + length / 2);
};
var angularDistance = function (left, right) {
    var delta = Math.abs(normalizeAngle(left) - normalizeAngle(right));
    return Math.min(delta, TWO_PI - delta);
};
var isAngleInGap = function (angle, start, end) {
    var normalized = normalizeAngle(angle);
    if (end >= start) {
        return normalized >= start && normalized <= end;
    }
    return normalized >= start || normalized <= end;
};
var minDistanceToBlockedIntervals = function (angle, blockedIntervals) {
    if (blockedIntervals.length === 0) {
        return Math.PI;
    }
    var minDistance = Math.PI;
    for (var _i = 0, blockedIntervals_1 = blockedIntervals; _i < blockedIntervals_1.length; _i++) {
        var interval = blockedIntervals_1[_i];
        if (isAngleInGap(angle, interval.start, interval.end)) {
            return 0;
        }
        minDistance = Math.min(minDistance, angularDistance(angle, interval.start), angularDistance(angle, interval.end));
    }
    return minDistance;
};
var gapCandidateAngles = function (start, end) {
    var candidates = [midpointAngle(start, end)];
    if (isAngleInGap(DEFAULT_LABEL_ANGLE, start, end)) {
        candidates.push(DEFAULT_LABEL_ANGLE);
    }
    return candidates;
};
export var pickCommunicationChartOrgTeamLabelAngle = function (blockedIntervals) {
    var merged = mergeAngleIntervals(blockedIntervals);
    if (merged.length === 0) {
        return DEFAULT_LABEL_ANGLE;
    }
    var topClearance = minDistanceToBlockedIntervals(DEFAULT_LABEL_ANGLE, merged);
    if (topClearance >= TOP_ANGLE_MIN_CLEARANCE_RAD) {
        return DEFAULT_LABEL_ANGLE;
    }
    var gaps = [];
    for (var index = 0; index < merged.length; index += 1) {
        var current = merged[index];
        var next = merged[(index + 1) % merged.length];
        var start = current.end;
        var end = next.start;
        var size = intervalLength({ start: start, end: end });
        if (size > 0.01) {
            gaps.push({ start: start, end: end, size: size });
        }
    }
    if (gaps.length === 0) {
        return DEFAULT_LABEL_ANGLE;
    }
    var bestAngle = DEFAULT_LABEL_ANGLE;
    var bestScore = Number.NEGATIVE_INFINITY;
    for (var _i = 0, gaps_1 = gaps; _i < gaps_1.length; _i++) {
        var gap = gaps_1[_i];
        for (var _a = 0, _b = gapCandidateAngles(gap.start, gap.end); _a < _b.length; _a++) {
            var candidate = _b[_a];
            var clearance = minDistanceToBlockedIntervals(candidate, merged);
            var topBias = 1 - angularDistance(candidate, DEFAULT_LABEL_ANGLE) / Math.PI;
            var score = gap.size + clearance * 0.75 + topBias * TOP_ANGLE_BIAS_WEIGHT;
            if (score > bestScore) {
                bestScore = score;
                bestAngle = candidate;
            }
        }
    }
    return bestAngle;
};
var buildVisibleEdgeKey = function (node, nodeId) {
    var center = node.renderedPosition();
    if (!isFiniteCommunicationChartPosition(center)) {
        return {
            visibleEdgeKey: '',
            blockedIntervals: [],
            maxArrowDepthPx: 0
        };
    }
    var blockedIntervals = [];
    var edgeKeyParts = [];
    var maxArrowDepthPx = 0;
    node.connectedEdges().forEach(function (edge) {
        if (edge.hasClass('hubHidden')) {
            return;
        }
        var arrowSize = readArrowSizeOnNode(edge, nodeId);
        edgeKeyParts.push("".concat(edge.id(), ":").concat(arrowSize));
        if (arrowSize !== 'none') {
            maxArrowDepthPx = Math.max(maxArrowDepthPx, resolveCommunicationChartArrowWidthPx(arrowSize, getEdgeRenderedLineWidthPx(edge)));
        }
        var endpoint = readNodeEndpoint(edge, nodeId);
        if (endpoint === null) {
            return;
        }
        var approachAngle = angleFromCenterToPoint(center, endpoint);
        var halfWidth = arrowSize !== 'none'
            ? ARROW_BLOCK_HALF_ANGLE_RAD
            : LINE_BLOCK_HALF_ANGLE_RAD;
        addBlockedInterval(blockedIntervals, approachAngle, halfWidth);
    });
    edgeKeyParts.sort();
    return {
        visibleEdgeKey: edgeKeyParts.join('|'),
        blockedIntervals: blockedIntervals,
        maxArrowDepthPx: maxArrowDepthPx
    };
};
var resolveLabelPlacementGeometry = function (node) {
    var renderedWidth = node.renderedWidth();
    var nodeId = node.id();
    var _a = buildVisibleEdgeKey(node, nodeId), visibleEdgeKey = _a.visibleEdgeKey, blockedIntervals = _a.blockedIntervals, maxArrowDepthPx = _a.maxArrowDepthPx;
    var cached = labelPlacementCache.get(node);
    if (cached &&
        cached.renderedWidth === renderedWidth &&
        cached.visibleEdgeKey === visibleEdgeKey) {
        return cached;
    }
    var angle = pickCommunicationChartOrgTeamLabelAngle(blockedIntervals);
    var radius = getRenderedCommunicationChartNodeRadiusPx(node);
    var labelOffset = radius + ORG_TEAM_LABEL_MARGIN_PX + maxArrowDepthPx * 0.45;
    var geometry = {
        renderedWidth: renderedWidth,
        visibleEdgeKey: visibleEdgeKey,
        cosAngle: Math.cos(angle),
        sinAngle: Math.sin(angle),
        labelOffset: labelOffset
    };
    labelPlacementCache.set(node, geometry);
    return geometry;
};
export var collectCommunicationChartOrgTeamBlockedAngles = function (node) { return buildVisibleEdgeKey(node, node.id()).blockedIntervals; };
export var resolveCommunicationChartOrgTeamLabelPlacement = function (node) {
    var center = node.renderedPosition();
    var _a = resolveLabelPlacementGeometry(node), cosAngle = _a.cosAngle, sinAngle = _a.sinAngle, labelOffset = _a.labelOffset;
    if (!isFiniteCommunicationChartPosition(center)) {
        return {
            x: 0,
            y: 0,
            textAlign: 'center',
            textBaseline: 'middle'
        };
    }
    var x = center.x + cosAngle * labelOffset;
    var y = center.y + sinAngle * labelOffset;
    var textAlign = cosAngle < -0.35 ? 'right' : cosAngle > 0.35 ? 'left' : 'center';
    var textBaseline = sinAngle < -0.35 ? 'bottom' : sinAngle > 0.35 ? 'top' : 'middle';
    return { x: x, y: y, textAlign: textAlign, textBaseline: textBaseline };
};
